import React from "react";
import Link from "next/link";
import Image from "next/image";
import { removeSlash } from "@/utils/common";
import styles from "./style.module.scss";

interface ExecutiveTeamProps {
  block: any;
  slug: string;
}

const imgTypes: any = {
  email: "/images/leadership/email.svg",
  linkedin: "/images/leadership/linkedin.svg",
};
const ExecutiveTeam: React.FC<ExecutiveTeamProps> = ({ block }) => {
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";
  return (
    <section className="relative z-[0] overflow-hidden py-[56px] lg:py-[80px] xl:py-[100px] bg-white">
      <div className="container">
        <div className="heading xl:mb-[40px] md:mb-[30px] mb-[24px]">
          <h2 className="text-heading-1 font-bold text-primary-text text-center">
            {block?.title ?? "Meet Our Executive Team"}
          </h2>
        </div>
        <div className="grid xl:grid-cols-3 sm:grid-cols-2 grid-cols-1  lg:gap-x-[30px] md:gap-x-[24px] gap-x-[16px] gap-y-[20px] md:gap-y-[30px] lg:gap-y-[40px]">
          {block?.team?.map((item: any, index: number) => (
            <div
              key={"team" + index}
              className="team-card relative h-[560px] lg:h-[700px] xl:h-[560px] 2xl:h-[650px] group overflow-hidden"
            >
              {/* Front Card */}
              <div className="absolute inset-0 w-full h-full transition-all duration-500 ease-in-out opacity-100 group-hover:opacity-0 ">
                <div className="relative w-full h-full before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.8)_80%)]">
                  {item?.image?.data?.attributes?.url && (
                    <Image
                      width={390}
                      height={560}
                      alt={"Executive Team Image" + index}
                      src={`${baseUrl}${item.image.data.attributes.url}`}
                      className="object-cover w-full  h-[560px] lg:h-[700px] xl:h-[560px] 2xl:h-[650px]"
                    />
                  )}
                  <div className="absolute bottom-[0px] left-[0px] p-[24px]">
                    <h5 className="text-[18px] leading-[25.2px] lg:text-[22px] lg:leading-[28px] xl:text-[24px] xl:leading-[31.2px] font-bold text-white mb-[8px]">
                      {item?.name ?? ""}
                    </h5>
                    <p className="text-[14px] leading-[19.6px] font-bold text-[#EDEDEE]">
                      {item?.position ?? ""}
                    </p>
                  </div>
                </div>
              </div>

              {/* Back Card */}
              <div className="absolute inset-0 w-full h-full opacity-0 transition-all duration-500 ease-in-out group-hover:opacity-100 group-hover:translate-y-0">
                {/* Blurred Image */}
                <div className="absolute inset-0 w-full h-full">
                  {item?.image?.data?.attributes?.url && (
                    <Image
                      width={390}
                      height={560}
                      alt={"Executive Team Image " + index + index}
                      src={`${baseUrl}${item.image.data.attributes.url}`}
                      className="object-cover w-full h-[560px] lg:h-[700px] xl:h-[560px] 2xl:h-[650px] filter blur-[4px] "
                    />
                  )}
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0.8)_100%)]"></div>
                </div>

                {/* Content */}
                <div className="absolute p-[32px] top-0 left-0 flex flex-col justify-between h-full z-10">
                  <div>
                    <div className="mb-[64px]">
                      <h5 className="text-[18px] leading-[25.2px] lg:text-[22px] lg:leading-[28px] xl:text-[24px] xl:leading-[31.2px] font-bold text-white mb-[8px]">
                        {item?.name ?? ""}
                      </h5>
                      <p className="text-[14px] leading-[19.6px] font-[400] text-[#EDEDEE]">
                        {item?.position ?? ""}
                      </p>
                    </div>
                    <p className="text-[16px] leading-[22.2px] lg:text-[18px] lg:leading-[25.2px] font-[400] text-white transition-all duration-500 group-hover:translate-y-[-32px]">
                      {item?.description ?? ""}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    {item?.learn_more?.href && (
                      <Link
                        href={`/our-leadership/${removeSlash(item.learn_more.href)}`}
                        className={`${styles.hoverLink} text-white text-[16px] leading-[22.2px] lg:text-[18px] lg:leading-[25.2px] font-semibold`}
                      >
                        {item?.learn_more?.label ?? "View More"}
                        <Image
                          src="/images/leadership/arrow-left.svg"
                          alt="arrow left"
                          width={20}
                          height={20}
                          className="ml-[10px] inline w-[20px] h-[20px]"
                        />
                      </Link>
                    )}
                    <div className="flex items-center gap-[22px] ml-auto">
                      {item?.social_link?.map(
                        (item: any, index: number) =>
                          item?.href && (
                            <Link
                              href={item.href}
                              key={"social-link" + index}
                              className="cursor-pointer group"
                            >
                              <Image
                                width={24}
                                height={24}
                                src={
                                  imgTypes[item?.label?.toLowerCase()] ??
                                  "/images/leadership/linkedin.svg"
                                }
                                alt={item?.label ?? "social"}
                                className="w-[20px] h-[20px] md:w-[24px] md:h-[24px] inline hover:scale-[1.1] transition-all duration-300 ease-in-out"
                              />
                            </Link>
                          ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExecutiveTeam;
